import { memo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import laptopApi from "../../../api/laptopApi";

// ── Types ─────────────────────────────────────────────────────────────────────
interface Laptop {
    id: number;
    model: string;
    brand: string;
    price: number;
    serialNumber: string;
    ram: number;
    storage: number;
    screenSize: number;
    gpu: string;
    cpu: string;
    batteryStatus: number;
    condition: string;
    description: string;
}

interface LaptopForm {
    model: string;
    brand: string;
    price: number | "";
    serialNumber: string;
    ram: number | "";
    storage: number | "";
    screenSize: number | "";
    gpu: string;
    cpu: string;
    batteryStatus: number | "";
    condition: string;
    description: string;
}

const EMPTY_FORM: LaptopForm = {
    model: "", brand: "", price: "", serialNumber: "",
    ram: "", storage: "", screenSize: "", gpu: "",
    cpu: "", batteryStatus: "", condition: "Like New", description: "",
};

const CONDITIONS = ["Like New", "Good", "Fair", "Poor"];
const LIMIT = 10;

// ── Modal ─────────────────────────────────────────────────────────────────────
function Modal({
    title, onClose, onSubmit, form, setForm, loading,
}: {
    title: string;
    onClose: () => void;
    onSubmit: () => void;
    form: LaptopForm;
    setForm: (f: LaptopForm) => void;
    loading: boolean;
}) {
    const field = (
        label: string,
        key: keyof LaptopForm,
        type: string = "text",
        placeholder: string = ""
    ) => (
        <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</label>
            <input
                type={type}
                value={form[key] as string}
                onChange={e => setForm({ ...form, [key]: type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })}
                placeholder={placeholder}
                className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
            />
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl mx-4 shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700">
                    <h2 className="text-lg font-bold text-white">{title}</h2>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-4 grid grid-cols-2 gap-4 max-h-[65vh] overflow-y-auto">
                    {field("Model", "model", "text", "MacBook Pro 16")}
                    {field("Hãng", "brand", "text", "Apple")}
                    {field("Serial Number", "serialNumber", "text", "SN123456")}
                    {field("Giá thuê / ngày (₫)", "price", "number", "2000000")}
                    {field("RAM (GB)", "ram", "number", "16")}
                    {field("Ổ cứng (GB)", "storage", "number", "512")}
                    {field("Màn hình (inch)", "screenSize", "number", "15.6")}
                    {field("Pin (%)", "batteryStatus", "number", "100")}
                    {field("CPU", "cpu", "text", "Intel Core i7")}
                    {field("GPU", "gpu", "text", "NVIDIA RTX 3060")}

                    {/* Condition select */}
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Tình trạng</label>
                        <select
                            value={form.condition}
                            onChange={e => setForm({ ...form, condition: e.target.value })}
                            className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 transition"
                        >
                            {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>

                    {/* Description full width */}
                    <div className="flex flex-col gap-1 col-span-2">
                        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Mô tả</label>
                        <textarea
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            rows={3}
                            placeholder="Mô tả laptop..."
                            className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition resize-none"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm text-slate-300 hover:text-white border border-slate-600 rounded-lg transition"
                    >
                        Huỷ
                    </button>
                    <button
                        onClick={onSubmit}
                        disabled={loading}
                        className="px-5 py-2 text-sm font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg transition flex items-center gap-2"
                    >
                        {loading && (
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                            </svg>
                        )}
                        Lưu
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Delete Confirm ────────────────────────────────────────────────────────────
function DeleteConfirm({ laptop, onClose, onConfirm, loading }: {
    laptop: Laptop; onClose: () => void; onConfirm: () => void; loading: boolean;
}) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-sm mx-4 shadow-2xl p-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                        <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </div>
                    <div>
                        <h3 className="font-bold text-white">Xoá laptop</h3>
                        <p className="text-sm text-slate-400">Hành động này không thể hoàn tác</p>
                    </div>
                </div>
                <p className="text-sm text-slate-300 mb-6">
                    Bạn có chắc muốn xoá <strong className="text-white">{laptop.model}</strong> ({laptop.brand})?
                </p>
                <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 py-2 text-sm border border-slate-600 text-slate-300 hover:text-white rounded-lg transition">Huỷ</button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className="flex-1 py-2 text-sm font-semibold bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded-lg transition flex items-center justify-center gap-2"
                    >
                        {loading && <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>}
                        Xoá
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────
function LaptopRental() {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);

    // Modal states
    const [showCreate, setShowCreate] = useState(false);
    const [editTarget, setEditTarget] = useState<Laptop | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<Laptop | null>(null);
    const [form, setForm] = useState<LaptopForm>(EMPTY_FORM);

    // ── Query
    const { data, isLoading } = useQuery<any>({
        queryKey: ["laptop-list-admin", { page, limit: LIMIT }],
        queryFn: () => laptopApi.getAllLaptop({ page, limit: LIMIT }),
        placeholderData: (prev: any) => prev,
    });

    const laptops: Laptop[] = data?.items ?? [];
    const totalPages: number = data?.meta?.totalPages ?? 0;
    const totalItems: number = data?.meta?.totalItems ?? 0;

    // ── Mutations
    const createMutation = useMutation({
        mutationFn: (d: any) => laptopApi.createLaptop(d),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["laptop-list-admin"] }); toast.success("Thêm laptop thành công!"); setShowCreate(false); },
        onError: () => toast.error("Thêm thất bại!"),
    });

    const updateMutation = useMutation({
        mutationFn: ({ data, id }: { data: any; id: string }) => laptopApi.updateLaptop(data, id),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["laptop-list-admin"] }); toast.success("Cập nhật thành công!"); setEditTarget(null); },
        onError: () => toast.error("Cập nhật thất bại!"),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => laptopApi.deleteLaptop(id),
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["laptop-list-admin"] }); toast.success("Đã xoá laptop!"); setDeleteTarget(null); },
        onError: () => toast.error("Xoá thất bại!"),
    });

    // ── Handlers
    const openCreate = () => { setForm(EMPTY_FORM); setShowCreate(true); };
    const openEdit = (l: Laptop) => {
        setForm({ model: l.model, brand: l.brand, price: l.price, serialNumber: l.serialNumber, ram: l.ram, storage: l.storage, screenSize: l.screenSize, gpu: l.gpu, cpu: l.cpu, batteryStatus: l.batteryStatus, condition: l.condition, description: l.description });
        setEditTarget(l);
    };
    const handleCreate = () => createMutation.mutate(form);
    const handleUpdate = () => editTarget && updateMutation.mutate({ data: form, id: String(editTarget.id) });
    const handleDelete = () => deleteTarget && deleteMutation.mutate(String(deleteTarget.id));

    const conditionColor: Record<string, string> = {
        "Like New": "text-emerald-400 bg-emerald-400/10",
        "Good": "text-blue-400 bg-blue-400/10",
        "Fair": "text-yellow-400 bg-yellow-400/10",
        "Poor": "text-red-400 bg-red-400/10",
    };

    return (
        <div className="text-white p-5 pt-3 w-full min-h-screen">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold">Quản lý Thuê Laptop</h1>
                    <p className="text-slate-400 text-sm mt-0.5">Tổng: <strong className="text-white">{totalItems}</strong> laptop</p>
                </div>
                <button
                    onClick={openCreate}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-xl transition"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Thêm laptop
                </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto rounded-xl border border-slate-700 bg-slate-800/50">
                <table className="min-w-full text-sm">
                    <thead>
                        <tr className="border-b border-slate-700 text-xs uppercase tracking-wider text-slate-400">
                            <th className="px-4 py-3 text-left">#</th>
                            <th className="px-4 py-3 text-left">Model</th>
                            <th className="px-4 py-3 text-left">Hãng</th>
                            <th className="px-4 py-3 text-left">CPU</th>
                            <th className="px-4 py-3 text-left">RAM</th>
                            <th className="px-4 py-3 text-left">SSD</th>
                            <th className="px-4 py-3 text-left">Tình trạng</th>
                            <th className="px-4 py-3 text-right">Giá / ngày</th>
                            <th className="px-4 py-3 text-center">Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            Array.from({ length: LIMIT }).map((_, i) => (
                                <tr key={i} className="border-b border-slate-700/50 animate-pulse">
                                    {Array.from({ length: 9 }).map((__, j) => (
                                        <td key={j} className="px-4 py-3"><div className="h-4 bg-slate-700 rounded w-3/4" /></td>
                                    ))}
                                </tr>
                            ))
                        ) : laptops.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="px-4 py-12 text-center text-slate-500">Chưa có laptop nào</td>
                            </tr>
                        ) : (
                            laptops.map((l, idx) => (
                                <tr key={l.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                                    <td className="px-4 py-3 text-slate-500 font-mono text-xs">{(page - 1) * LIMIT + idx + 1}</td>
                                    <td className="px-4 py-3 font-medium text-white">{l.model}</td>
                                    <td className="px-4 py-3 text-slate-300">{l.brand}</td>
                                    <td className="px-4 py-3 text-slate-400 max-w-[160px] truncate">{l.cpu}</td>
                                    <td className="px-4 py-3 text-slate-300">{l.ram}GB</td>
                                    <td className="px-4 py-3 text-slate-300">{l.storage}GB</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${conditionColor[l.condition] ?? "text-slate-400 bg-slate-700"}`}>
                                            {l.condition}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right font-semibold text-blue-400">
                                        {l.price.toLocaleString("vi-VN")} ₫
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => openEdit(l)}
                                                className="p-1.5 rounded-lg text-slate-400 hover:text-blue-400 hover:bg-blue-400/10 transition"
                                                title="Sửa"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => setDeleteTarget(l)}
                                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-400/10 transition"
                                                title="Xoá"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-end gap-1 mt-4">
                    <button disabled={page === 1} onClick={() => setPage(1)} className="px-2.5 py-1.5 text-sm rounded-lg border border-slate-600 disabled:opacity-40 hover:bg-slate-700 transition text-slate-300">«</button>
                    <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 text-sm rounded-lg border border-slate-600 disabled:opacity-40 hover:bg-slate-700 transition text-slate-300">‹</button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                        return (
                            <button key={p} onClick={() => setPage(p)} className={`px-3 py-1.5 text-sm rounded-lg border transition ${p === page ? "bg-blue-600 text-white border-blue-600" : "border-slate-600 text-slate-300 hover:bg-slate-700"}`}>{p}</button>
                        );
                    })}
                    <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 text-sm rounded-lg border border-slate-600 disabled:opacity-40 hover:bg-slate-700 transition text-slate-300">›</button>
                    <button disabled={page === totalPages} onClick={() => setPage(totalPages)} className="px-2.5 py-1.5 text-sm rounded-lg border border-slate-600 disabled:opacity-40 hover:bg-slate-700 transition text-slate-300">»</button>
                </div>
            )}

            {/* Modals */}
            {showCreate && (
                <Modal title="Thêm laptop mới" onClose={() => setShowCreate(false)} onSubmit={handleCreate} form={form} setForm={setForm} loading={createMutation.isPending} />
            )}
            {editTarget && (
                <Modal title={`Sửa: ${editTarget.model}`} onClose={() => setEditTarget(null)} onSubmit={handleUpdate} form={form} setForm={setForm} loading={updateMutation.isPending} />
            )}
            {deleteTarget && (
                <DeleteConfirm laptop={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleteMutation.isPending} />
            )}
        </div>
    );
}

export default memo(LaptopRental);